import type {
  WidgetPublicConfig,
  WidgetModality,
  WidgetPrecallField,
  WidgetPrecallForm,
  TranscriptEntry,
  WidgetPill,
  WidgetPillParameter,
} from './types';

export function initialRatingValue(field: Pick<WidgetPrecallField, 'max' | 'ratingDefault'>): number {
  const stars = Math.min(10, Math.max(2, Number(field.max) || 5));
  return field.ratingDefault === 'full' ? stars : 0;
}
import { buildStyles, COMPOSER_MAX_HEIGHT } from './styles';
import { renderRichContent, type RichContentDirective } from './richContent';
import { buildLinkPreview, findLinks, type LinkPreviewResolver } from './linkPreview';
import { VoiceAvatarController, type VoiceAvatarState } from './voice-avatar';
import { widgetErrorMessage } from './errors';

// Lightweight DOM helpers — keeps the bundle free of any framework
// runtime while still giving us a structured render path.

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, any> = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
    else if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

/** Escape HTML so user/agent text can never inject markup. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Accept only image URLs that cannot execute script. Branding values are
 * admin-supplied but still travel through the public config, so `javascript:`
 * and other active schemes are rejected outright.
 */
function safeImageUrl(value: unknown): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,[a-z0-9+/=]+$/i.test(raw)) return raw;
  return null;
}

/** Segment count for the composer's live mic meter. */
const MIC_METER_SEGMENTS = 12;

/**
 * Render a minimal, safe subset of Markdown (bold, italic, line breaks, links)
 * for agent messages. Links are lifted out before escaping and re-inserted as
 * our own anchors, so this stays XSS-safe. Returns a span ready to append.
 */
function renderMarkdown(text: string): HTMLSpanElement {
  const span = document.createElement('span');
  const source = text.replace(/\u0000/g, '');
  const links = findLinks(source);
  let lifted = '';
  let cursor = 0;
  links.forEach((link, i) => {
    lifted += `${source.slice(cursor, link.start)}\u0000${i}\u0000`;
    cursor = link.end;
  });
  lifted += source.slice(cursor);
  let html = escapeHtml(lifted);
  // Bold: **text** or __text__
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  // Italic: *text* or _text_ (avoid matching inside already-bolded spans)
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  html = html.replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>');
  // Line breaks
  html = html.replace(/\n/g, '<br>');
  html = html.replace(/\u0000(\d+)\u0000/g, (_m, i) => {
    const link = links[Number(i)];
    const href = escapeHtml(link.href).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    return `<a class="msg-link" href="${href}" target="_blank" rel="noopener noreferrer nofollow">${escapeHtml(link.label)}</a>`;
  });
  span.innerHTML = html;
  return span;
}

export type WidgetUiEvent =
  | { type: 'open' }
  | { type: 'minimize' }
  | { type: 'end.session' }
  | { type: 'chat.cancel' }
  | { type: 'chat.retry' }
  | { type: 'postcall.submit'; data: Record<string, string> }
  | { type: 'postcall.skip' }
  | { type: 'precall.submit'; modality: WidgetModality; data: Record<string, string> }
  | { type: 'metadata.consent'; acceptedCategories: string[] }
  | { type: 'modality.change'; modality: WidgetModality }
  | { type: 'send.text'; text: string }
  | { type: 'draft.changed'; text: string }
  | { type: 'mic.toggle' }
  | { type: 'mic.down' }
  | { type: 'mic.up' }
  | { type: 'file.select'; files: File[] }
  | { type: 'file.remove'; id: string }
  | { type: 'rich.submit'; toolCallId: string; formData: Record<string, any> }
  | { type: 'feedback.submit'; messageId: string; answer: string; question?: string; rating: 'up' | 'down'; comment?: string }
  | { type: 'pill.click'; pill: WidgetPill; parameters?: Record<string, any> }
  | { type: 'agent.stop' }
  | { type: 'end' };

export interface WidgetUiFileEntry {
  id: string;        // local id until upload completes (then server fileId)
  name: string;
  mimeType: string;
  size: number;
  status: 'uploading' | 'ready' | 'failed';
  message?: string;  // error text or summary
}

export class WidgetUi {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private root: HTMLDivElement;
  private cfg: WidgetPublicConfig;
  private listener: (e: WidgetUiEvent) => void = () => {};
  /** Called when the visitor starts a voice clip — lets the controller stop
   *  any live (streamed) agent audio so the two never play at once. */
  private onClipPlay: () => void = () => {};

  private panel: HTMLDivElement | null = null;
  private launcherEl: HTMLButtonElement | null = null;
  private helpBubbleEl: HTMLDivElement | null = null;
  private helpBubbleTimer: number | null = null;
  private helpBubbleConsumed = false;
  private helpBubbleMessageEl: HTMLButtonElement | null = null;
  private helpBubblePhrases: string[] = [];
  private helpPhraseIndex = 0;
  private helpPhraseTimer: number | null = null;
  private body: HTMLDivElement | null = null;
  private statusEl: HTMLDivElement | null = null;
  private statusDotEl: HTMLSpanElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private sendBtn: HTMLButtonElement | null = null;
  private micBtn: HTMLButtonElement | null = null;
  private micIcoEl: HTMLSpanElement | null = null;
  private micLabelEl: HTMLSpanElement | null = null;
  private micMeterEl: HTMLDivElement | null = null;
  private voiceControlEl: HTMLDivElement | null = null;
  /** Realtime voice: the in-bubble ■ STOP button + whether one is currently wanted. */
  private agentStopBtn: HTMLButtonElement | null = null;
  private wantAgentStop = false;
  private micMode: 'mute' | 'push-to-talk' | 'hold-to-talk' = 'mute';
  private micMuted = false;
  private micRecording = false;
  private micHoldMode = false;
  /** Continuous open-mic: true while the agent is verbalizing (mic auto-muted). */
  private agentSpeaking = false;
  /** WhatsApp-style composer: the input row, its recording overlay + timer. */
  private inputRowEl: HTMLDivElement | null = null;
  private recOverlay: HTMLDivElement | null = null;
  private recTimerEl: HTMLSpanElement | null = null;
  private recTimer: number | null = null;
  /** Poller that keeps a form's submit button in sync with autofilled values. */
  private formSyncTimer: number | null = null;
  private recStartMs = 0;
  /** Loading panel shown while the session/tools/knowledge spin up. */
  private loadingEl: HTMLDivElement | null = null;
  private fileBtn: HTMLButtonElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private filesEl: HTMLDivElement | null = null;
  private fileChips: Map<string, HTMLDivElement> = new Map();
  /** True while at least one attachment is still uploading (send stays disabled). */
  private uploadingFiles = false;
  /** True when at least one ready attachment is queued to send. */
  private hasReadyFiles = false;
  private partialEl: HTMLDivElement | null = null;
  /** The single live quick-reply chip row, if any. */
  private pillRowEl: HTMLElement | null = null;
  private pillFormEl: HTMLElement | null = null;
  private typingEl: HTMLDivElement | null = null;
  private modality: WidgetModality;
  private sessionActive = false;
  private voiceAvatarEl: HTMLDivElement | null = null;
  private voiceAvatarLabelEl: HTMLDivElement | null = null;
  private voiceAvatar: VoiceAvatarController | null = null;
  private voiceAvatarState: VoiceAvatarState = 'idle';
  private pendingScrollToLatest = false;
  private linkPreviewResolver: LinkPreviewResolver | null = null;

  /**
   * When the widget's responseMode is 'audio' (audio only), agent replies are
   * voice; we suppress the transcript text bubble so the visitor sees only the
   * audio player. 'audio_text'/'text'/'match' keep the default text rendering.
   */
  private hideAgentText = false;
  private humanChatMode = false;
  private transcript: TranscriptEntry[] = [];

  // Minimal in-bundle i18n table — language is set per-widget by the
  // admin and read via tr() so launcher/header strings are translated
  // without depending on the host app's i18n provider.
  private static readonly STRINGS: Record<string, Record<string, string>> = {
    en: {
      helpOffer: 'Hi! How can we help you today?',
      close: 'Close', minimize: 'Minimize', endConversation: 'End conversation', resumeChat: 'Resume chat',
      typeMessage: 'Type a message…', connecting: 'Connecting…', connected: 'Connected', chatWithUs: 'Chat with us',
      submit: 'Submit', skip: 'Skip', thankYou: 'Thanks for your feedback!', postcallTitle: 'How was your experience?',
      send: 'Send', muteUnmute: 'Mute / unmute', attachFile: 'Attach file',
      modalityText: 'Text', modalityVoice: 'Voice', beforeWeStart: 'Before we start', start: 'Start',
      poweredBy: 'Powered by BitFractal', conversationEnded: 'Conversation ended', ready: 'Ready',
      cannotStart: 'Could not start session', cannotConnect: 'Could not connect',
      micDenied: 'Microphone access denied', connectionError: 'Connection error',
      tryAgain: 'Sorry, that took longer than expected. Please try again.',
      listening: 'Listening… tap to send', thinking: 'Thinking…', tapToSpeak: 'Tap to speak',
      holdToSpeak: 'Hold to talk', tapToTalk: 'Tap to talk', micOpen: 'Mic on — tap to mute',
      micMuted: 'Muted — tap to turn on', listeningRelease: 'Listening… release to send',
      recordingTap: 'Recording… tap to send', voiceHint: 'Voice is on — use the mic, or type below',
      recording: 'Recording…',
      agentSpeaking: 'Agent speaking — tap to interrupt', stop: 'Stop',
      listeningShort: 'Listening', agentSpeakingShort: 'Agent speaking',
      aiDisclaimer: 'AI-generated responses may be inaccurate. Please verify important information.',
      transcriptEmailLabel: 'Email me the conversation transcript',
      transcriptEmailPlaceholder: 'you@example.com',
      yes: 'Yes', no: 'No', npsLow: 'Not likely', npsHigh: 'Very likely',
      feedbackPrompt: 'Rate this response:', feedbackUp: 'Helpful', feedbackDown: 'Not helpful',
      feedbackCommentLabel: 'Tell us how we could improve:', feedbackSend: 'send suggestion',
      feedbackThanks: 'Thanks for your feedback!',
      playVideo: 'Play video',
      privacyChoices: 'Your privacy choices', privacyMessage: 'Choose which optional information may be used during this conversation.',
      privacyEssential: 'Essential session information', privacyPersonalization: 'Personalization', privacyAnalytics: 'Analytics',
      privacyPreciseLocation: 'Precise location', privacyEssentialOnly: 'Essential only', privacyContinue: 'Continue',
    },
    pt: {
      helpOffer: 'Olá! Como podemos ajudar você hoje?',
      close: 'Fechar', minimize: 'Minimizar', endConversation: 'Encerrar conversa', resumeChat: 'Continuar conversa',
      typeMessage: 'Digite uma mensagem…', connecting: 'Conectando…', connected: 'Conectado', chatWithUs: 'Fale conosco',
      submit: 'Enviar', skip: 'Pular', thankYou: 'Obrigado pelo seu feedback!', postcallTitle: 'Como foi sua experiência?',
      send: 'Enviar', muteUnmute: 'Silenciar / ativar microfone', attachFile: 'Anexar arquivo',
      modalityText: 'Texto', modalityVoice: 'Voz', beforeWeStart: 'Antes de começar', start: 'Iniciar',
      poweredBy: 'Desenvolvido por BitFractal', conversationEnded: 'Conversa encerrada', ready: 'Pronto',
      cannotStart: 'Não foi possível iniciar a sessão', cannotConnect: 'Não foi possível conectar',
      micDenied: 'Acesso ao microfone negado', connectionError: 'Erro de conexão',
      tryAgain: 'Desculpe, isso demorou mais que o esperado. Tente novamente.',
      listening: 'Ouvindo… toque para enviar', thinking: 'Pensando…', tapToSpeak: 'Toque para falar',
      holdToSpeak: 'Segure para falar', tapToTalk: 'Toque para falar', micOpen: 'Microfone ligado — toque para silenciar',
      micMuted: 'Silenciado — toque para ativar', listeningRelease: 'Ouvindo… solte para enviar',
      recordingTap: 'Gravando… toque para enviar', voiceHint: 'Voz ativada — use o microfone, ou digite abaixo',
      recording: 'Gravando…',
      agentSpeaking: 'Agente falando — toque para interromper', stop: 'Parar',
      listeningShort: 'Ouvindo', agentSpeakingShort: 'Agente falando',
      aiDisclaimer: 'Respostas geradas por IA podem conter imprecisões. Confira sempre as informações importantes.',
      transcriptEmailLabel: 'Quero receber a transcrição da conversa por e-mail',
      transcriptEmailPlaceholder: 'voce@exemplo.com',
      yes: 'Sim', no: 'Não', npsLow: 'Pouco provável', npsHigh: 'Muito provável',
      feedbackPrompt: 'Avaliar a resposta:', feedbackUp: 'Útil', feedbackDown: 'Não ajudou',
      feedbackCommentLabel: 'Preencha a sugestão de melhoria:', feedbackSend: 'enviar sugestão',
      feedbackThanks: 'Obrigado pelo seu feedback!',
      playVideo: 'Reproduzir vídeo',
      privacyChoices: 'Suas escolhas de privacidade', privacyMessage: 'Escolha quais informações opcionais podem ser usadas durante esta conversa.',
      privacyEssential: 'Informações essenciais da sessão', privacyPersonalization: 'Personalização', privacyAnalytics: 'Análise',
      privacyPreciseLocation: 'Localização precisa', privacyEssentialOnly: 'Somente essenciais', privacyContinue: 'Continuar',
    },
    es: {
      helpOffer: '¡Hola! ¿Cómo podemos ayudarte hoy?',
      close: 'Cerrar', minimize: 'Minimizar', endConversation: 'Finalizar conversación', resumeChat: 'Continuar conversación',
      typeMessage: 'Escribe un mensaje…', connecting: 'Conectando…', connected: 'Conectado', chatWithUs: 'Habla con nosotros',
      submit: 'Enviar', skip: 'Omitir', thankYou: '¡Gracias por tus comentarios!', postcallTitle: '¿Cómo fue tu experiencia?',
      send: 'Enviar', muteUnmute: 'Silenciar / activar micrófono', attachFile: 'Adjuntar archivo',
      modalityText: 'Texto', modalityVoice: 'Voz', beforeWeStart: 'Antes de comenzar', start: 'Iniciar',
      poweredBy: 'Desarrollado por BitFractal', conversationEnded: 'Conversación finalizada', ready: 'Listo',
      cannotStart: 'No se pudo iniciar la sesión', cannotConnect: 'No se pudo conectar',
      micDenied: 'Acceso al micrófono denegado', connectionError: 'Error de conexión',
      tryAgain: 'Lo sentimos, esto tardó más de lo esperado. Inténtalo de nuevo.',
      listening: 'Escuchando… toca para enviar', thinking: 'Pensando…', tapToSpeak: 'Toca para hablar',
      holdToSpeak: 'Mantén para hablar', tapToTalk: 'Toca para hablar', micOpen: 'Micrófono activo — toca para silenciar',
      micMuted: 'Silenciado — toca para activar', listeningRelease: 'Escuchando… suelta para enviar',
      recordingTap: 'Grabando… toca para enviar', voiceHint: 'Voz activada — usa el micrófono, o escribe abajo',
      recording: 'Grabando…',
      agentSpeaking: 'Agente hablando — toca para interrumpir', stop: 'Detener',
      listeningShort: 'Escuchando', agentSpeakingShort: 'Agente hablando',
      aiDisclaimer: 'Las respuestas generadas por IA pueden ser imprecisas. Verifica siempre la información importante.',
      transcriptEmailLabel: 'Quiero recibir la transcripción de la conversación por correo',
      transcriptEmailPlaceholder: 'tu@ejemplo.com',
      yes: 'Sí', no: 'No', npsLow: 'Poco probable', npsHigh: 'Muy probable',
      feedbackPrompt: 'Califica la respuesta:', feedbackUp: 'Útil', feedbackDown: 'No ayudó',
      feedbackCommentLabel: 'Cuéntanos cómo podemos mejorar:', feedbackSend: 'enviar sugerencia',
      feedbackThanks: '¡Gracias por tus comentarios!',
      playVideo: 'Reproducir video',
      privacyChoices: 'Tus opciones de privacidad', privacyMessage: 'Elige qué información opcional se puede utilizar durante esta conversación.',
      privacyEssential: 'Información esencial de la sesión', privacyPersonalization: 'Personalización', privacyAnalytics: 'Analítica',
      privacyPreciseLocation: 'Ubicación precisa', privacyEssentialOnly: 'Solo esenciales', privacyContinue: 'Continuar',
    },
  };

  tr(key: string, fallback: string): string {
    const lang = ((this.cfg as any).language || '').toLowerCase().split('-')[0];
    return WidgetUi.STRINGS[lang]?.[key] || WidgetUi.STRINGS.en[key] || fallback;
  }

  constructor(cfg: WidgetPublicConfig, preview = false) {
    this.cfg = cfg;
    const mods: any = cfg.modalities || {};
    this.modality = mods.defaultMode || mods.default || (mods.voice ? 'voice' : 'text');
    this.hideAgentText = mods.responseMode === 'audio';
    this.host = document.createElement('div');
    this.host.setAttribute('data-autoqm-widget', cfg.publicKey);
    this.shadow = this.host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = buildStyles(cfg, preview);
    this.shadow.appendChild(style);
    this.root = el('div', { class: 'root' });
    this.shadow.appendChild(this.root);
    document.body.appendChild(this.host);
    this.renderLauncher();
  }

  on(fn: (e: WidgetUiEvent) => void): void { this.listener = fn; }

  /** Register a callback fired whenever the visitor starts a voice clip. */
  setOnClipPlay(fn: () => void): void { this.onClipPlay = fn; }

  /** Fetches page metadata for link cards; without one only media links preview. */
  setLinkPreviewResolver(fn: LinkPreviewResolver | null): void { this.linkPreviewResolver = fn; }

  /** WhatsApp-style: the first link in an agent message gets a card above the text. */
  private attachLinkPreview(bubble: HTMLElement, text: string): void {
    if (!text || bubble.querySelector('.link-preview')) return;
    const first = findLinks(text)[0];
    if (!first) return;
    const card = buildLinkPreview(first.href, {
      resolver: this.linkPreviewResolver,
      playLabel: this.tr('playVideo', 'Play video'),
      onResize: () => {
        const body = this.body;
        // Follow the newest message only while the visitor is reading near the bottom.
        if (body && body.scrollHeight - body.scrollTop - body.clientHeight < 420) this.scrollToLatest();
      },
    });
    if (!card) return;
    bubble.classList.add('has-link-preview');
    bubble.insertBefore(card, bubble.firstChild);
  }

  /** Play / pause glyphs for the WhatsApp-style voice-clip player. */
  private static readonly PLAY_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
  private static readonly PAUSE_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';
  /** Filled square glyph for the agent-bubble ■ STOP button. */
  private static readonly STOP_ICON =
    '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><rect x="5" y="5" width="14" height="14" rx="2" fill="currentColor"/></svg>';

  private emit(e: WidgetUiEvent): void { try { this.listener(e); } catch { /* */ } }

  destroy(): void {
    this.dismissHelpBubble();
    this.voiceAvatar?.destroy();
    this.voiceAvatar = null;
    try { this.host.remove(); } catch { /* */ }
  }

  // ── Screens ───────────────────────────────────────────────────────────

  renderLauncher(): void {
    this.clear();
    if (this.cfg.behavior?.showLauncher === false) return;
    this.mountLauncher();
  }

  /** Append the launcher button without clearing other elements. Used by minimize. */
  private mountLauncher(): void {
    if (this.cfg.behavior?.showLauncher === false) return;
    if (this.launcherEl && this.launcherEl.isConnected) {
      this.launcherEl.style.display = '';
      this.updateLauncherActiveDot();
      return;
    }
    const b: any = this.cfg.branding || {};
    const baseLabel = b.launcherLabel || b.launcherText || this.tr('chatWithUs', 'Chat with us');
    const label = this.sessionActive ? this.tr('resumeChat', baseLabel) : baseLabel;
    const launcherIcon = b.launcherIcon;
    const inner: (Node | string)[] = launcherIcon
      ? [el('img', { src: launcherIcon, alt: '', style: { width: '60%', height: '60%', objectFit: 'contain', borderRadius: '50%' } }) as unknown as Node]
      : [iconChat() as unknown as Node];
    const btn = el('button', {
      class: 'launcher',
      title: label,
      onClick: () => this.emit({ type: 'open' }),
    }, inner) as HTMLButtonElement;
    this.launcherEl = btn;
    this.root.appendChild(btn);
    this.updateLauncherActiveDot();
    this.scheduleHelpBubble();
  }

  private scheduleHelpBubble(): void {
    const config = this.cfg.behavior?.helpBubble;
    if (!config?.enabled || this.helpBubbleConsumed || this.helpBubbleTimer !== null || this.sessionActive || this.isPanelVisible()) return;
    const configuredDelay = Number(config.delaySeconds ?? 10);
    const delay = Number.isFinite(configuredDelay) ? Math.min(3600, Math.max(1, configuredDelay)) : 10;
    this.helpBubbleTimer = window.setTimeout(() => {
      this.helpBubbleTimer = null;
      if (this.helpBubbleConsumed || this.sessionActive || this.isPanelVisible() || !this.launcherEl?.isConnected || this.launcherEl.style.display === 'none') return;
      this.helpBubbleConsumed = true;
      this.helpBubblePhrases = this.resolveHelpBubblePhrases(config);
      const effect = ['pulse', 'glow', 'bounce', 'shake', 'float'].includes(String(config.effect))
        ? ` fx-${config.effect}` : '';
      const image = safeImageUrl(config.imageUrl);
      const messageEl = el('button', {
        class: 'help-bubble-message',
        onClick: () => { this.dismissHelpBubble(); this.emit({ type: 'open' }); },
      }, [this.helpBubblePhrases[0]]) as HTMLButtonElement;
      this.helpBubbleMessageEl = messageEl;
      this.helpBubbleEl = el('div', { class: `help-bubble${image ? ' has-image' : ''}${effect}`, role: 'status', 'aria-live': 'polite' }, [
        ...(image ? [el('img', { class: 'help-bubble-image', src: image, alt: '' }) as unknown as Node] : []),
        messageEl,
        el('button', { class: 'help-bubble-dismiss', title: this.tr('close', 'Close'), 'aria-label': this.tr('close', 'Close'), onClick: () => { this.dismissHelpBubble(); this.launcherEl?.focus(); } }, ['×']),
      ]);
      this.root.appendChild(this.helpBubbleEl);
      if (config.highlightLauncher) this.launcherEl.classList.add('attention');
      if (this.helpBubblePhrases.length > 1) this.scheduleNextHelpPhrase(config);
    }, delay * 1000);
  }

  /** `message` is phrase one, `messages` the timed follow-ups; empty falls back to the localized default. */
  private resolveHelpBubblePhrases(config: NonNullable<WidgetPublicConfig['behavior']>['helpBubble']): string[] {
    const first = typeof config?.message === 'string' ? config.message.trim().slice(0, 240) : '';
    const rest = Array.isArray(config?.messages) ? config!.messages! : [];
    const phrases = [first, ...rest.map(entry => String(entry || '').trim().slice(0, 240))].filter(Boolean).slice(0, 8);
    return phrases.length ? phrases : [this.tr('helpOffer', 'Hi! How can we help you today?')];
  }

  /** Swap in the next phrase; stops at the last one unless the config loops. */
  private scheduleNextHelpPhrase(config: NonNullable<NonNullable<WidgetPublicConfig['behavior']>['helpBubble']>): void {
    const configuredInterval = Number(config.intervalSeconds ?? 6);
    const interval = Number.isFinite(configuredInterval) ? Math.min(600, Math.max(2, configuredInterval)) : 6;
    this.helpPhraseTimer = window.setTimeout(() => {
      this.helpPhraseTimer = null;
      if (!this.helpBubbleEl?.isConnected || !this.helpBubbleMessageEl) return;
      const next = this.helpPhraseIndex + 1;
      if (next >= this.helpBubblePhrases.length && !config.loop) return;
      this.helpPhraseIndex = next % this.helpBubblePhrases.length;
      this.helpBubbleMessageEl.textContent = this.helpBubblePhrases[this.helpPhraseIndex];
      this.helpBubbleMessageEl.classList.remove('is-entering');
      void this.helpBubbleMessageEl.offsetWidth;
      this.helpBubbleMessageEl.classList.add('is-entering');
      this.scheduleNextHelpPhrase(config);
    }, interval * 1000);
  }

  private dismissHelpBubble(): void {
    this.helpBubbleConsumed = true;
    if (this.helpBubbleTimer !== null) window.clearTimeout(this.helpBubbleTimer);
    this.helpBubbleTimer = null;
    if (this.helpPhraseTimer !== null) window.clearTimeout(this.helpPhraseTimer);
    this.helpPhraseTimer = null;
    this.launcherEl?.classList.remove('attention');
    this.helpBubbleEl?.remove();
    this.helpBubbleEl = null;
    this.helpBubbleMessageEl = null;
  }

  private updateLauncherActiveDot(): void {
    if (!this.launcherEl) return;
    const existing = this.launcherEl.querySelector('.active-dot');
    if (this.sessionActive && !existing) {
      this.launcherEl.appendChild(el('span', { class: 'active-dot' }));
    } else if (!this.sessionActive && existing) {
      existing.remove();
    }
  }

  setSessionActive(active: boolean): void {
    if (active) this.dismissHelpBubble();
    this.sessionActive = active;
    this.updateLauncherActiveDot();
    if (this.statusDotEl) {
      this.statusDotEl.classList.toggle('connected', active);
      if (active) this.statusDotEl.title = this.tr('connected', 'Connected');
      else this.statusDotEl.title = this.tr('connecting', 'Connecting…');
    }
  }

  /** Hide the panel (keep its DOM + refs intact) and show launcher. */
  minimizePanel(): void {
    if (this.panel) this.panel.style.display = 'none';
    this.mountLauncher();
  }

  /** Show the previously-built panel; hide the launcher. */
  restorePanel(): void {
    if (!this.panel) return;
    this.dismissHelpBubble();
    this.panel.style.display = 'flex';
    if (this.launcherEl) this.launcherEl.style.display = 'none';
    this.scrollToLatest();
  }

  /** True when a panel is mounted and not minimized. */
  isPanelVisible(): boolean {
    return !!this.panel && this.panel.style.display !== 'none';
  }

  /** Show the panel; optionally render the precall form first. */
  openPanel(showPrecall: boolean, showMetadataConsent = false): void {
    this.dismissHelpBubble();
    this.clear();
    this.panel = el('div', { class: 'panel' });
    this.panel.appendChild(this.renderHeader());
    if (showMetadataConsent) {
      this.panel.appendChild(this.renderMetadataConsent());
    } else if (showPrecall && this.cfg.precallForm?.enabled && this.cfg.precallForm.fields?.length) {
      this.panel.appendChild(this.renderPrecall());
    } else {
      this.panel.appendChild(this.renderConversation());
    }
    this.root.appendChild(this.panel);
  }

  continueAfterMetadataConsent(showPrecall: boolean): void {
    if (!this.panel) return;
    for (const stale of Array.from(this.panel.querySelectorAll('.precall, .panel-body'))) stale.remove();
    this.panel.appendChild(showPrecall ? this.renderPrecall() : this.renderConversation());
  }

  private renderMetadataConsent(): HTMLElement {
    const metadata = this.cfg.metadata!;
    const categories = new Map<string, string>();
    for (const field of metadata.fields || []) {
      if (field.enabled === false || field.category === 'essential') continue;
      const label = field.category === 'precise_location' ? this.tr('privacyPreciseLocation', 'Precise location')
        : field.category === 'personalization' ? this.tr('privacyPersonalization', 'Personalization') : this.tr('privacyAnalytics', 'Analytics');
      categories.set(field.category, label);
    }
    const accepted = new Set<string>(['essential']);
    const wrap = el('div', { class: 'precall metadata-consent' });
    wrap.appendChild(el('h3', {}, [metadata.noticeTitle || this.tr('privacyChoices', 'Your privacy choices')]));
    wrap.appendChild(el('p', { class: 'form-description' }, [metadata.noticeText || this.tr('privacyMessage', 'Choose which optional information may be used during this conversation.')]));
    wrap.appendChild(el('label', { class: 'field field-check' }, [
      el('input', { type: 'checkbox', checked: true, disabled: true }),
      el('span', {}, [this.tr('privacyEssential', 'Essential session information')]),
    ]));
    for (const [category, label] of categories) {
      const checkbox = el('input', { type: 'checkbox' }) as HTMLInputElement;
      checkbox.addEventListener('change', () => checkbox.checked ? accepted.add(category) : accepted.delete(category));
      wrap.appendChild(el('label', { class: 'field field-check' }, [checkbox, el('span', {}, [label])]));
    }
    const actions = el('div', { class: 'consent-actions' });
    actions.appendChild(el('button', {
      class: 'submit secondary',
      onClick: () => this.emit({ type: 'metadata.consent', acceptedCategories: ['essential'] }),
    }, [metadata.declineLabel || this.tr('privacyEssentialOnly', 'Essential only')]));
    actions.appendChild(el('button', {
      class: 'submit',
      onClick: () => this.emit({ type: 'metadata.consent', acceptedCategories: Array.from(accepted) }),
    }, [metadata.acceptLabel || this.tr('privacyContinue', 'Continue')]));
    wrap.appendChild(actions);
    return wrap;
  }

  private renderHeader(): HTMLElement {
    const b: any = this.cfg.branding || {};
    const realtimeAvatar = b.voiceAvatarStyle && b.voiceAvatarStyle !== 'none';
    const title = realtimeAvatar
      ? (b.title || b.headerTitle || 'Virtual Agent')
      : (b.agentName || b.title || b.headerTitle || 'Virtual Agent');
    const initial = String(title).charAt(0).toUpperCase();
    const avatarSrc = safeImageUrl(b.avatarUrl || b.logoUrl);
    const avatar = avatarSrc
      ? el('img', { class: 'avatar', src: avatarSrc, alt: '' })
      : el('div', { class: 'avatar' }, [initial]);
    this.statusDotEl = el('span', {
      class: 'status-dot',
      title: this.tr('connecting', 'Connecting…'),
    }) as HTMLSpanElement;
    // Optional brand header: a logo lockup plus custom colors / background
    // image, for embeds that need to match the host site (see the widget
    // Appearance tab). Only http(s) and data:image URLs are accepted.
    const headerLogo = safeImageUrl(b.headerLogoUrl);
    const headerBg = safeImageUrl(b.headerBackgroundImageUrl);
    const headerStyle: Record<string, string> = {};
    if (b.headerBackgroundColor) headerStyle.background = b.headerBackgroundColor;
    if (headerBg) {
      headerStyle.backgroundImage = `url("${headerBg}")`;
      headerStyle.backgroundSize = 'cover';
      headerStyle.backgroundPosition = 'center';
    }
    if (b.headerTextColor) headerStyle.color = b.headerTextColor;
    if (b.headerHeight) {
      const h = Math.min(160, Math.max(40, Number(b.headerHeight) || 64));
      headerStyle.minHeight = `${h}px`;
    }
    const align: string = ['left', 'center', 'right'].includes(b.headerAlign) ? b.headerAlign : 'left';
    // Logo + titles live in one lockup so `headerAlign` can slide the whole
    // group left/center/right while the action buttons stay pinned right.
    const lockup = el('div', { class: 'header-lockup' }, [
      ...(headerLogo
        ? [el('img', {
            class: 'header-logo',
            src: headerLogo,
            alt: '',
            style: { height: `${Math.min(64, Math.max(16, Number(b.headerLogoHeight) || 32))}px` },
          })]
        : realtimeAvatar ? [] : [avatar]),
      el('div', { class: 'header-titles' }, [
        el('div', { class: 'title-row' }, [
          el('span', { class: 'title' }, [title]),
          this.statusDotEl,
        ]),
        el('div', { class: 'subtitle' }, [b.subtitle || '']),
      ]),
    ]);
    const header = el('div', { class: `header header-align-${align}`, style: headerStyle }, [
      lockup,
      el('button', {
        title: this.tr('minimize', 'Minimize'),
        onClick: () => this.emit({ type: 'minimize' }),
      }, ['×']),
    ]);
    if (b.headerTextColor) header.classList.add('header-custom-text');
    return header;
  }

  private renderPrecall(): HTMLElement {
    const f = this.cfg.precallForm;
    const readers: Record<string, () => string> = {};
    const wrap = el('div', { class: 'precall' });
    wrap.appendChild(el('h3', {}, [f.title || this.tr('beforeWeStart', 'Before we start')]));
    if (f.description) wrap.appendChild(el('p', { class: 'form-description' }, [f.description]));

    for (const field of f.fields || []) {
      wrap.appendChild(this.renderPrecallField(field, readers));
    }
    const submit = el('button', {
      class: 'submit',
      onClick: (ev: Event) => {
        ev.preventDefault();
        const collected = this.collectFormData(f.fields || [], readers);
        if (!collected) {
          (submit as HTMLButtonElement).disabled = false;
          return;
        }
        (submit as HTMLButtonElement).disabled = true;
        this.emit({ type: 'precall.submit', modality: this.modality, data: collected });
      },
    }, [f.submitLabel || this.tr('start', 'Start')]);
    wrap.appendChild(submit);
    this.bindSubmitState(wrap, submit as HTMLButtonElement, f.fields || [], readers);
    return wrap;
  }

  /**
   * Keeps the submit button's enabled state in sync with the required answers,
   * so the visitor sees it go live the moment the form is complete.
   */
  private bindSubmitState(
    wrap: HTMLElement,
    submit: HTMLButtonElement,
    fields: WidgetPrecallField[],
    readers: Record<string, () => string>,
  ): void {
    const sync = (): void => {
      submit.disabled = fields.some(f => f.required && !(readers[f.id]?.() || '').trim());
    };
    wrap.addEventListener('input', sync);
    wrap.addEventListener('change', sync);
    // Star ratings are buttons, so their value only settles after the click.
    wrap.addEventListener('click', () => setTimeout(sync, 0));
    // Browser autofill populates inputs without firing any event, so poll while
    // the form is on screen.
    if (this.formSyncTimer !== null) window.clearInterval(this.formSyncTimer);
    this.formSyncTimer = window.setInterval(() => {
      if (!wrap.isConnected) {
        if (this.formSyncTimer !== null) window.clearInterval(this.formSyncTimer);
        this.formSyncTimer = null;
        return;
      }
      sync();
    }, 400);
    sync();
  }

  /**
   * Reads every field value; returns null when a required answer is missing.
   * Answers are keyed by `contextKey` so the runtime can lay them straight
   * into the agent's context bag (falling back to the field id).
   */
  private collectFormData(
    fields: WidgetPrecallField[],
    readers: Record<string, () => string>,
  ): Record<string, string> | null {
    const data: Record<string, string> = {};
    for (const field of fields) {
      const val = (readers[field.id]?.() || '').trim();
      if (field.required && !val) return null;
      if (val) data[field.contextKey || field.id] = val;
    }
    return data;
  }

  private renderPrecallField(
    field: WidgetPrecallField,
    readers: Record<string, () => string>,
  ): HTMLElement {
    const label = `${field.label || ''}${field.required ? ' *' : ''}`;

    if (field.type === 'checkbox' || field.type === 'consent') {
      const box = el('input', { type: 'checkbox', name: field.id }) as HTMLInputElement;
      readers[field.id] = () => (box.checked ? 'true' : '');
      return this.wrapField(el('label', { class: 'field field-check' }, [box, el('span', {}, [label])]), field);
    }

    if (field.type === 'rating') {
      return this.wrapField(this.renderRatingField(field, readers, label), field);
    }

    if (field.type === 'slider') {
      return this.wrapField(this.renderSliderField(field, readers, label), field);
    }

    if (field.type === 'transcript_email') {
      return this.wrapField(this.renderTranscriptEmailField(field, readers, label), field);
    }

    if (field.type === 'nps' || field.type === 'yes_no') {
      return this.wrapField(this.renderChoiceButtonsField(field, readers, label), field);
    }

    let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (field.type === 'select') {
      input = el('select', { name: field.id, required: field.required }) as HTMLSelectElement;
      // Leading blank so a required select can't silently submit option #1.
      input.appendChild(el('option', { value: '' }, [field.placeholder || '—']));
      for (const opt of field.options || []) {
        input.appendChild(el('option', { value: opt }, [opt]));
      }
    } else if (field.type === 'textarea') {
      input = el('textarea', {
        name: field.id, rows: '3', placeholder: field.placeholder || '',
        required: field.required, maxlength: field.maxLength,
      }) as HTMLTextAreaElement;
    } else {
      input = el('input', {
        // `phone` is the editor's name for the HTML `tel` input type.
        type: field.type === 'phone' ? 'tel' : field.type,
        name: field.id, placeholder: field.placeholder || '',
        required: field.required, maxlength: field.maxLength,
        ...(field.type === 'number' && Number.isFinite(Number(field.min)) ? { min: String(field.min) } : {}),
        ...(field.type === 'number' && Number.isFinite(Number(field.max)) ? { max: String(field.max) } : {}),
      }) as HTMLInputElement;
    }
    readers[field.id] = () => input.value || '';
    return this.wrapField(el('label', { class: 'field' }, [label, input]), field);
  }

  /** Appends the optional help text under a rendered field. */
  private wrapField(node: HTMLElement, field: WidgetPrecallField): HTMLElement {
    if (!field.helpText) return node;
    const wrap = el('div', { class: 'field-wrap' }, [node]);
    wrap.appendChild(el('div', { class: 'field-help' }, [field.helpText]));
    return wrap;
  }

  private renderRatingField(
    field: WidgetPrecallField,
    readers: Record<string, () => string>,
    label: string,
  ): HTMLElement {
    const stars = Math.min(10, Math.max(2, Number(field.max) || 5));
    let value = initialRatingValue(field);
    const buttons: HTMLButtonElement[] = [];
    const paint = (upTo: number): void => {
      buttons.forEach((b, i) => b.classList.toggle('on', i < upTo));
    };
    const row = el('div', { class: 'rating-row', role: 'radiogroup' });
    for (let i = 1; i <= stars; i++) {
      const btn = el('button', {
        type: 'button',
        class: 'rating-star',
        'aria-label': String(i),
        onClick: () => { value = i; paint(i); },
      }, ['★']) as HTMLButtonElement;
      btn.addEventListener('mouseenter', () => paint(i));
      btn.addEventListener('mouseleave', () => paint(value));
      buttons.push(btn);
      row.appendChild(btn);
    }
    paint(value);
    readers[field.id] = () => (value ? String(value) : '');
    return el('div', { class: 'field field-rating' }, [el('span', { class: 'field-label' }, [label]), row]);
  }

  /** NPS (0-10) and Yes/No as a single-select row of buttons. */
  private renderChoiceButtonsField(
    field: WidgetPrecallField,
    readers: Record<string, () => string>,
    label: string,
  ): HTMLElement {
    const choices = field.type === 'nps'
      ? Array.from({ length: 11 }, (_, n) => ({ value: String(n), title: String(n) }))
      : [{ value: 'yes', title: this.tr('yes', 'Yes') }, { value: 'no', title: this.tr('no', 'No') }];
    let value = '';
    const buttons: HTMLButtonElement[] = [];
    const row = el('div', { class: `choice-row${field.type === 'nps' ? ' choice-row-nps' : ''}`, role: 'radiogroup' });
    for (const choice of choices) {
      const btn = el('button', {
        type: 'button',
        class: 'choice-btn',
        'aria-label': choice.title,
        onClick: () => {
          value = choice.value;
          buttons.forEach(b => b.classList.toggle('on', b === btn));
        },
      }, [choice.title]) as HTMLButtonElement;
      buttons.push(btn);
      row.appendChild(btn);
    }
    readers[field.id] = () => value;
    const children: Node[] = [el('span', { class: 'field-label' }, [label]), row];
    if (field.type === 'nps') {
      children.push(el('div', { class: 'choice-scale' }, [
        el('span', {}, [this.tr('npsLow', 'Not likely')]),
        el('span', {}, [this.tr('npsHigh', 'Very likely')]),
      ]));
    }
    return el('div', { class: 'field field-choice' }, children);
  }

  private renderSliderField(
    field: WidgetPrecallField,
    readers: Record<string, () => string>,
    label: string,
  ): HTMLElement {
    const min = Number.isFinite(Number(field.min)) ? Number(field.min) : 0;
    const max = Number.isFinite(Number(field.max)) && Number(field.max) > min ? Number(field.max) : 10;
    const step = Number(field.step) > 0 ? Number(field.step) : 1;
    const start = Math.round((min + max) / 2);
    const out = el('span', { class: 'slider-value' }, [String(start)]);
    const input = el('input', {
      type: 'range', name: field.id,
      min: String(min), max: String(max), step: String(step), value: String(start),
    }) as HTMLInputElement;
    input.addEventListener('input', () => { out.textContent = input.value; });
    readers[field.id] = () => input.value || '';
    return el('div', { class: 'field field-slider' }, [
      el('span', { class: 'field-label' }, [label]),
      el('div', { class: 'slider-row' }, [
        el('span', { class: 'slider-bound' }, [String(min)]),
        input,
        el('span', { class: 'slider-bound' }, [String(max)]),
        out,
      ]),
    ]);
  }

  /**
   * Opt-in "email me the transcript" question: a checkbox that reveals an
   * email box. The collected value is the address (blank when declined), which
   * the SDK forwards to the configured alert tool once the session is over.
   */
  private renderTranscriptEmailField(
    field: WidgetPrecallField,
    readers: Record<string, () => string>,
    label: string,
  ): HTMLElement {
    const email = el('input', {
      type: 'email',
      name: `${field.id}_email`,
      placeholder: field.placeholder || this.tr('transcriptEmailPlaceholder', 'you@example.com'),
    }) as HTMLInputElement;
    email.hidden = true;
    const box = el('input', { type: 'checkbox', name: field.id }) as HTMLInputElement;
    box.addEventListener('change', () => {
      email.hidden = !box.checked;
      if (box.checked) email.focus();
    });
    readers[field.id] = () => (box.checked ? (email.value || '').trim() : '');
    return el('div', { class: 'field field-transcript' }, [
      el('label', { class: 'field-check' }, [box, el('span', {}, [label || this.tr('transcriptEmailLabel', 'Email me the conversation transcript')])]),
      email,
    ]);
  }

  renderConversation(): HTMLElement {
    this.humanChatMode = false;
    // Replaces the pre-call form in place — leaving it behind stacks the form
    // above the transcript and breaks the panel layout.
    if (this.panel) {
      for (const stale of Array.from(this.panel.querySelectorAll('.precall, .panel-body'))) stale.remove();
    }
    this.transcript = [];
    const wrap = el('div', { class: 'panel-body', style: { display: 'flex', flexDirection: 'column', flex: '1', minHeight: '0' } });

    const avatarStyle = this.cfg.branding?.voiceAvatarStyle || 'none';
    if (avatarStyle !== 'none') {
      const canvas = el('canvas', { class: 'voice-avatar-canvas', role: 'img' }) as HTMLCanvasElement;
      this.voiceAvatarLabelEl = el('div', { class: 'voice-avatar-label', 'aria-live': 'polite' }, [this.tr('ready', 'Ready')]) as HTMLDivElement;
      // Expanded is an avatar-only stage (transcript hidden), matching the AI
      // IVR tester. The toggle is omitted when the admin locks the layout.
      const startExpanded = this.cfg.branding?.voiceLayoutDefault === 'expanded';
      const switchable = this.cfg.branding?.voiceLayoutSwitchable !== false;
      const children: Node[] = [canvas, this.voiceAvatarLabelEl];
      if (switchable) {
        children.push(el('button', {
          class: 'voice-avatar-layout',
          type: 'button',
          title: this.tr('avatarLayout', 'Switch voice layout'),
          onClick: () => this.toggleVoiceAvatarLayout(),
        }, ['⤢']));
      }
      this.voiceAvatarEl = el('div', {
        class: `voice-avatar-stage ${startExpanded ? 'immersive' : 'compact'}`,
        hidden: 'hidden',
      }, children) as HTMLDivElement;
      wrap.appendChild(this.voiceAvatarEl);
      // `avatar-immersive` hides the transcript, so it is only applied once the
      // voice stage is actually visible (see setVoiceAvatarActive). Applying it
      // up front blanks the panel for text sessions, which have no stage.
      const theme: any = this.cfg.theme || {};
      this.voiceAvatar = new VoiceAvatarController(canvas, {
        style: avatarStyle,
        primaryColor: this.cfg.branding.voiceAvatarPrimaryColor || '#fd6262',
        accentColor: this.cfg.branding.voiceAvatarAccentColor || '#60a5fa',
        orbitEnabled: this.cfg.branding.voiceAvatarOrbitEnabled !== false,
        orbitScale: this.cfg.branding.voiceAvatarOrbitScale ?? 1.25,
        imageUrl: safeImageUrl(this.cfg.branding.voiceAvatarImageUrl) || undefined,
      });
    }

    // The textual greeting / status row was removed in favor of the small
    // pulsing dot rendered next to the header title. `setStatus()` now
    // updates the dot's tooltip + connected class only.
    this.body = el('div', { class: 'body' });
    wrap.appendChild(this.body);

    const footer = el('div', { class: 'footer' });
    // WhatsApp-style composer: a single rounded text field with the mic (or the
    // send button once the visitor types) on the right and the optional "+"
    // attach button on the left — used for ALL modalities, voice included.
    const row = el('div', { class: 'input-row' });
    this.inputRowEl = row as HTMLDivElement;

    // Left: WhatsApp-style "+" attach button. Built first so it sits to the LEFT
    // of the text field; the hidden file input is appended at the end of the row.
    if (this.cfg.files?.enabled) {
      const allowed = (this.cfg.files.allowedMimeTypes || []).join(',');
      this.fileInput = el('input', {
        type: 'file',
        multiple: this.cfg.files.maxFiles && this.cfg.files.maxFiles > 1 ? 'multiple' : null,
        accept: allowed || null,
        style: { display: 'none' },
        onChange: (ev: Event) => {
          const target = ev.target as HTMLInputElement;
          const files = Array.from(target.files || []);
          if (files.length > 0) this.emit({ type: 'file.select', files });
          target.value = ''; // allow re-selecting the same file
        },
      }) as HTMLInputElement;
      this.fileBtn = el('button', {
        class: 'attach-btn',
        title: this.cfg.files.uploadLabel || this.tr('attachFile', 'Attach file'),
        onClick: () => this.fileInput?.click(),
      }, ['+']) as HTMLButtonElement;
      row.appendChild(this.fileBtn);
    }

    this.inputEl = el('textarea', {
      rows: '1',
      placeholder: (this.cfg.branding as any)?.inputPlaceholder || this.tr('typeMessage', 'Type a message…'),
      onKeydown: (ev: KeyboardEvent) => {
        if (ev.key === 'Enter' && !ev.shiftKey) {
          ev.preventDefault();
          this.sendCurrent();
        }
      },
      onInput: () => { this.autoGrowInput(); this.updateComposerMode(); this.emit({ type: 'draft.changed', text: this.getDraft() }); },
    }) as HTMLTextAreaElement;
    row.appendChild(this.inputEl);

    this.sendBtn = el('button', {
      class: 'send-btn',
      title: this.tr('send', 'Send'),
      onClick: () => this.sendCurrent(),
    }, ['➤']) as HTMLButtonElement;
    row.appendChild(this.sendBtn);

    if (this.cfg.modalities.voice) {
      this.micIcoEl = el('span', { class: 'mic-ico' }, ['🎤']) as HTMLSpanElement;
      this.micLabelEl = el('span', { class: 'mic-label' }, [this.tr('micMuted', 'Muted — tap to turn on')]) as HTMLSpanElement;
      // Live input meter, mirroring the AI IVR tester's VU bar, rendered inside
      // the button so the visitor sees the mic is picking them up.
      this.micMeterEl = el('div', { class: 'mic-meter', 'aria-hidden': 'true' }) as HTMLDivElement;
      for (let i = 0; i < MIC_METER_SEGMENTS; i++) {
        this.micMeterEl.appendChild(el('span', { class: 'mic-meter-seg' }));
      }
      this.micBtn = el('button', {
        class: 'mic-btn',
        title: this.tr('muteUnmute', 'Mute / unmute'),
        onClick: () => { if (!this.micHoldMode) this.emit({ type: 'mic.toggle' }); },
      }, [
        this.micIcoEl as unknown as Node,
        this.micLabelEl as unknown as Node,
        this.micMeterEl as unknown as Node,
      ]) as HTMLButtonElement;
      // Hold-to-talk pointer handlers (active only in hold-to-talk mode).
      const down = (ev: Event) => { if (this.micHoldMode) { ev.preventDefault(); this.emit({ type: 'mic.down' }); } };
      const up = (ev: Event) => { if (this.micHoldMode) { ev.preventDefault(); this.emit({ type: 'mic.up' }); } };
      this.micBtn.addEventListener('pointerdown', down);
      this.micBtn.addEventListener('pointerup', up);
      this.micBtn.addEventListener('pointerleave', up);
      this.micBtn.addEventListener('pointercancel', up);
      this.voiceControlEl = el('div', { class: 'voice-control' }, [this.micBtn]) as HTMLDivElement;
      row.classList.add('has-voice-control');
      this.updateMicHint();
    }

    // WhatsApp-style recording overlay: rendered OVER the text bar while a voice
    // clip is captured (push-to-talk / hold-to-talk). The mic button stays on
    // top so the visitor can release / tap to send.
    this.recTimerEl = el('span', { class: 'rec-time' }, ['0:00']) as HTMLSpanElement;
    this.recOverlay = el('div', { class: 'rec-overlay' }, [
      el('span', { class: 'rec-dot' }),
      el('span', { class: 'rec-label' }, [this.tr('recording', 'Recording…')]),
      this.recTimerEl as unknown as Node,
    ]) as HTMLDivElement;
    row.appendChild(this.recOverlay);

    if (this.cfg.files?.enabled && this.fileInput) row.appendChild(this.fileInput);

    footer.appendChild(row);
    if (this.voiceControlEl) footer.appendChild(this.voiceControlEl);
    this.updateComposerMode();

    if (this.cfg.files?.enabled) {
      // A fresh row means the previous chips are detached; keep the map in step
      // so upsert can't silently update an orphaned node.
      this.fileChips.clear();
      this.filesEl = el('div', { class: 'files-row' });
      footer.appendChild(this.filesEl);
    }

    // Explicit end control lives at the bottom of the panel — the old header
    // icon was too easy to miss.
    footer.appendChild(el('button', {
      class: 'end-conversation-btn',
      type: 'button',
      onClick: () => this.emit({ type: 'end.session' }),
    }, [
      iconDoor() as unknown as Node,
      el('span', {}, [(this.cfg.branding as any)?.endCallLabel || this.tr('endConversation', 'End conversation')]),
    ]));
    if ((this.cfg.branding as any)?.showAiDisclaimer) {
      footer.appendChild(el('div', { class: 'ai-disclaimer' }, [
        (this.cfg.branding as any)?.aiDisclaimerText
          || this.tr('aiDisclaimer', 'AI-generated responses may be inaccurate. Please verify important information.'),
      ]));
    }
    if ((this.cfg.branding as any)?.showPoweredBy !== false) {
      footer.appendChild(el('div', { class: 'powered' }, [(this.cfg.branding as any)?.poweredByText || this.tr('poweredBy', 'Powered by BitFractal')]));
    }
    wrap.appendChild(footer);

    // Append the body — caller has already inserted header.
    if (this.panel) this.panel.appendChild(wrap);
    return wrap;
  }

  private sendCurrent(): void {
    // A file is still uploading — the send button is disabled, but Enter could
    // still fire here; block until every attachment has settled.
    if (this.uploadingFiles || (this.humanChatMode && this.sendBtn?.disabled)) return;
    const text = (this.inputEl?.value || '').trim();
    // Allow sending when there is text OR at least one ready attachment.
    if (!text && !this.hasReadyFiles) return;
    if (this.inputEl && !this.humanChatMode) {
      this.inputEl.value = '';
      this.autoGrowInput();
    }
    this.updateComposerMode();
    this.emit({ type: 'send.text', text });
  }

  /**
   * WhatsApp-style composer: when the in-row mic is present, show the mic while
   * the field is empty and swap to the send button as soon as the visitor
   * types. Text-only agents (no in-row mic) always show the send button.
   */
  private updateComposerMode(): void {
    if (!this.inputRowEl) return;
    const hasText = !!(this.inputEl && this.inputEl.value.trim());
    // Ready attachments also reveal the send button so a file can be sent
    // without typing.
    this.inputRowEl.classList.toggle('has-text', hasText || this.hasReadyFiles);
  }

  private startRecTimer(): void {
    this.stopRecTimer();
    this.recStartMs = Date.now();
    const tick = (): void => {
      const s = Math.floor((Date.now() - this.recStartMs) / 1000);
      const m = Math.floor(s / 60);
      const ss = s % 60;
      if (this.recTimerEl) this.recTimerEl.textContent = `${m}:${ss < 10 ? '0' : ''}${ss}`;
    };
    tick();
    this.recTimer = window.setInterval(tick, 500);
  }

  private stopRecTimer(): void {
    if (this.recTimer !== null) { window.clearInterval(this.recTimer); this.recTimer = null; }
    if (this.recTimerEl) this.recTimerEl.textContent = '0:00';
  }

  /** Grow the input textarea with its content up to the CSS max-height, then scroll. */
  private autoGrowInput(): void {
    const ta = this.inputEl;
    if (!ta) return;
    ta.style.height = 'auto';
    const content = ta.scrollHeight;
    ta.style.height = `${Math.min(content, COMPOSER_MAX_HEIGHT)}px`;
    // A permanent `overflow-y: auto` renders a scrollbar on a single line, so
    // the composer only becomes scrollable once it has hit its ceiling.
    ta.style.overflowY = content > COMPOSER_MAX_HEIGHT ? 'auto' : 'hidden';
  }

  /** Show an animated "agent is typing" bubble (near-RT, while awaiting a reply). */
  showTyping(): void {
    if (!this.body || this.typingEl) return;
    if (this.cfg.behavior?.showTypingIndicator === false) return;
    const node = el('div', { class: 'msg agent typing' }, [
      el('span', { class: 'typing-dot' }),
      el('span', { class: 'typing-dot' }),
      el('span', { class: 'typing-dot' }),
    ]) as HTMLDivElement;
    this.typingEl = node;
    this.body.appendChild(this.wrapAgentRow(node));
    this.scrollToLatest();
  }

  /** Remove the typing indicator bubble if present. */
  hideTyping(): void {
    if (this.typingEl) {
      this.rowOf(this.typingEl).remove();
      this.typingEl = null;
    }
  }

  private scrollToLatest(): void {
    if (!this.body) return;
    if (!this.isPanelVisible()) {
      this.pendingScrollToLatest = true;
      return;
    }
    this.pendingScrollToLatest = false;
    const body = this.body;
    requestAnimationFrame(() => {
      if (this.body !== body || !body.isConnected || !this.isPanelVisible()) return;
      body.scrollTop = body.scrollHeight;
    });
  }

  // ── Agent avatar (message rows) ──────────────────────────────────────

  /** Build the small agent avatar shown to the left of agent message bubbles. */
  private makeAvatarEl(): HTMLElement {
    const b: any = this.cfg.branding || {};
    const src = b.avatarUrl || b.logoUrl;
    if (src) return el('img', { class: 'msg-avatar', src, alt: '' });
    const title = b.agentName || b.title || b.headerTitle || 'A';
    return el('div', { class: 'msg-avatar msg-avatar-initial' }, [String(title).charAt(0).toUpperCase()]);
  }

  /** Wrap an agent bubble in a flex row with the avatar to its left. */
  private wrapAgentRow(bubble: HTMLElement): HTMLElement {
    if (this.cfg.branding?.voiceAvatarStyle && this.cfg.branding.voiceAvatarStyle !== 'none') return bubble;
    return el('div', { class: 'msg-row agent' }, [this.makeAvatarEl(), bubble]);
  }

  /** Resolve the outermost element to remove for a bubble (its avatar row, if any). */
  private rowOf(node: Element): Element {
    return node.parentElement && node.parentElement.classList.contains('msg-row') ? node.parentElement : node;
  }

  // ── Updates from controller ──────────────────────────────────────────

  setStatus(text: string): void {
    // The visible status row was removed in favor of the header green dot.
    // We keep the API so callers don't break; the text is mirrored to the
    // dot's tooltip so power users can still inspect connection state.
    if (this.statusEl) this.statusEl.textContent = text;
    if (this.statusDotEl && text) this.statusDotEl.title = text;
  }

  errorMessage(error: unknown): string {
    return widgetErrorMessage(error, this.cfg.language);
  }

  openUnavailable(error: unknown): void {
    this.clear();
    this.panel = el('div', { class: 'panel' });
    this.panel.appendChild(this.renderHeader());
    this.root.appendChild(this.panel);
    this.setError(this.errorMessage(error));
  }

  setError(msg: string): void {
    if (!this.panel) return;
    const existing = this.shadow.querySelector('.error');
    if (existing) existing.textContent = msg;
    else this.panel.appendChild(el('div', { class: 'error', role: 'alert' }, [msg]));
  }

  clearError(): void { this.shadow.querySelector('.error')?.remove(); }

  setHumanChatStatus(title: string, status: string): void {
    const heading = this.shadow.querySelector('.header .title');
    const subtitle = this.shadow.querySelector('.header .subtitle');
    if (heading) heading.textContent = title;
    if (subtitle) subtitle.textContent = status;
    this.setStatus(status);
  }

  setHumanQueueCancel(label: string, visible: boolean, busy = false): void {
    const existing = this.shadow.querySelector<HTMLButtonElement>('.chat-cancel-btn');
    if (!visible) { existing?.remove(); return; }
    const button = existing || el('button', { class: 'end-conversation-btn chat-cancel-btn', type: 'button',
      onClick: () => this.emit({ type: 'chat.cancel' }) });
    button.textContent = label;
    button.disabled = busy;
    if (!existing) this.shadow.querySelector('.end-conversation-btn')?.before(button);
  }

  setHumanSendRetry(label: string, visible: boolean, busy = false): void {
    const existing = this.shadow.querySelector<HTMLButtonElement>('.chat-retry-btn');
    if (!visible) { existing?.remove(); return; }
    const button = existing || el('button', { class: 'end-conversation-btn chat-retry-btn', type: 'button',
      onClick: () => this.emit({ type: 'chat.retry' }) });
    button.textContent = label;
    button.disabled = busy;
    if (!existing) this.shadow.querySelector('.end-conversation-btn')?.before(button);
  }

  resumeIvaText(): void {
    this.humanChatMode = false;
    this.setHumanSendRetry('', false);
    this.setHumanQueueCancel('', false);
    this.setHumanChatStatus(this.cfg.branding?.title || 'Virtual Agent', this.tr('ready', 'Ready'));
    this.setSendEnabled(true);
    this.clearError();
  }

  appendMessage(entry: TranscriptEntry): void {
    this.transcript.push(entry);
    if (!this.body) return;
    this.hideLoading();
    this.hideTyping();
    // Audio-only response mode: render agent replies as voice bubbles (audio
    // player only), suppressing the spoken transcript text.
    const voiceOnly = (entry.kind === 'voice' && !entry.text)
      || (entry.role === 'agent' && this.hideAgentText);
    const node = el('div', {
      class: `msg ${entry.role}${entry.partial ? ' partial' : ''}${voiceOnly ? ' voice' : ''}`,
      'data-id': entry.id,
    });
    if (!voiceOnly) {
      // Agent text may contain lightweight Markdown (e.g. **bold**); render it.
      node.appendChild(entry.role === 'agent' ? renderMarkdown(entry.text) : document.createTextNode(entry.text));
    }
    if (entry.role === 'agent') this.attachLinkPreview(node, entry.text);
    // Attachments sent with this message: render a compact file row inside the bubble.
    if (entry.attachments && entry.attachments.length) {
      const filesWrap = el('div', { class: 'msg-files' });
      for (const att of entry.attachments) {
        filesWrap.appendChild(el('div', { class: 'msg-file', title: att.name }, [
          el('span', { class: 'msg-file-ico' }, ['📎']),
          el('span', { class: 'msg-file-name' }, [att.name]),
        ]));
      }
      node.appendChild(filesWrap);
    }
    // Voice clip / agent reply audio: user clips always show; agent audio is
    // gated by the widget's showMessageAudio config (default on).
    if (entry.audioUrl) {
      const allowAgentAudio = this.cfg.behavior?.showMessageAudio !== false;
      if (entry.role === 'user' || allowAgentAudio) node.appendChild(this.makeAudioPlayer(entry.audioUrl));
    }
    const time = this.makeTimeMeta(entry.ts);
    if (time) node.appendChild(time);
    this.body.appendChild(entry.role === 'agent' ? this.wrapAgentRow(node) : node);
    if (entry.role === 'agent' && !voiceOnly) this.attachFeedback(node, entry.id, entry.text);
    // While the agent is speaking, the ■ STOP button follows the newest agent
    // bubble — audio can stream before OR after its transcript bubble renders.
    if (entry.role === 'agent' && this.wantAgentStop) this.attachAgentStop(node);
    this.scrollToLatest();
  }

  // ── Per-response feedback ────────────────────────────────────────────

  /**
   * Append the 👍/👎 rating row under an agent bubble. The rating is emitted on
   * click so it is captured even when the visitor never writes a comment; the
   * comment send re-emits with the same messageId and the backend replaces it.
   */
  private attachFeedback(bubble: HTMLElement, messageId: string, answer: string): void {
    const fb: any = (this.cfg as any).feedback;
    if (!fb?.enabled) return;
    if (this.modality !== 'text') return;
    if (!answer || !answer.trim()) return;

    const row = el('div', { class: 'msg-feedback' });
    row.appendChild(el('span', { class: 'msg-feedback-label' }, [
      fb.promptLabel || this.tr('feedbackPrompt', 'Rate this response:'),
    ]));

    const upLabel = this.tr('feedbackUp', 'Helpful');
    const downLabel = this.tr('feedbackDown', 'Not helpful');
    const up = el('button', { type: 'button', class: 'msg-feedback-btn', title: upLabel, 'aria-label': upLabel }, ['👍']);
    const down = el('button', { type: 'button', class: 'msg-feedback-btn', title: downLabel, 'aria-label': downLabel }, ['👎']);
    row.appendChild(up);
    row.appendChild(down);

    const host = bubble.parentElement && bubble.parentElement.classList.contains('msg-row')
      ? bubble.parentElement
      : bubble;
    const question = this.precedingUserText(host);
    const anchor = el('div', { class: 'msg-feedback-anchor' });
    anchor.appendChild(row);
    host.insertAdjacentElement('afterend', anchor);

    const done = (): void => {
      anchor.textContent = '';
      anchor.appendChild(el('div', { class: 'msg-feedback-thanks' }, [
        fb.thanksLabel || this.tr('feedbackThanks', 'Thanks for your feedback!'),
      ]));
    };

    let formEl: HTMLElement | null = null;
    const pick = (rating: 'up' | 'down'): void => {
      up.classList.toggle('selected', rating === 'up');
      down.classList.toggle('selected', rating === 'down');
      this.emit({ type: 'feedback.submit', messageId, answer, question, rating });
      const wantsComment = fb.commentOn === 'both' || rating === 'down';
      if (!wantsComment) { done(); return; }
      if (formEl) { formEl.remove(); formEl = null; }
      formEl = this.buildFeedbackForm(fb, messageId, answer, question, rating, done);
      anchor.appendChild(formEl);
      this.scrollToLatest();
    };
    up.addEventListener('click', () => pick('up'));
    down.addEventListener('click', () => pick('down'));
  }

  /** Text of the visitor turn immediately above an agent bubble, if any. */
  private precedingUserText(host: Element): string | undefined {
    let cur: Element | null = host.previousElementSibling;
    while (cur) {
      const bubble = cur.classList.contains('msg') ? cur : cur.querySelector('.msg');
      if (bubble && bubble.classList.contains('user')) {
        return (bubble.textContent || '').trim().slice(0, 2000) || undefined;
      }
      cur = cur.previousElementSibling;
    }
    return undefined;
  }

  /** Free-text improvement-suggestion form revealed after a rating. */
  private buildFeedbackForm(
    fb: any,
    messageId: string,
    answer: string,
    question: string | undefined,
    rating: 'up' | 'down',
    onSent: () => void,
  ): HTMLElement {
    const wrap = el('div', { class: 'msg-feedback-form' });
    wrap.appendChild(el('label', { for: `fb-${messageId}` }, [
      fb.commentLabel || this.tr('feedbackCommentLabel', 'Tell us how we could improve:'),
    ]));
    const area = el('textarea', { id: `fb-${messageId}`, rows: '3', maxlength: '2000' }) as HTMLTextAreaElement;
    wrap.appendChild(area);
    const send = el('button', { type: 'button', class: 'msg-feedback-send' }, [
      fb.submitLabel || this.tr('feedbackSend', 'send suggestion'),
    ]);
    send.addEventListener('click', () => {
      const comment = area.value.trim();
      this.emit({ type: 'feedback.submit', messageId, answer, question, rating, comment });
      onSent();
    });
    wrap.appendChild(send);
    setTimeout(() => { try { area.focus(); } catch { /* */ } }, 0);
    return wrap;
  }

  /** Build a WhatsApp-style inline player for a voice clip / reply audio. */
  private makeAudioPlayer(url: string): HTMLElement {
    const wrap = el('div', { class: 'msg-audio' });
    const audio = el('audio', { src: url, preload: 'metadata' }) as HTMLAudioElement;
    const btn = el('button', { type: 'button', class: 'msg-audio-btn', 'aria-label': 'Play' });
    btn.innerHTML = WidgetUi.PLAY_ICON;
    const track = el('div', { class: 'msg-audio-track' });
    const fill = el('div', { class: 'msg-audio-fill' });
    const knob = el('div', { class: 'msg-audio-knob' });
    track.appendChild(fill);
    track.appendChild(knob);
    const timeEl = el('span', { class: 'msg-audio-time' }, ['0:00']);

    const fmt = (s: number): string => {
      if (!isFinite(s) || s < 0) s = 0;
      const m = Math.floor(s / 60);
      const ss = Math.floor(s % 60);
      return `${m}:${ss < 10 ? '0' : ''}${ss}`;
    };
    let dur = 0;
    const knownDur = (): number => (isFinite(audio.duration) && audio.duration > 0 ? audio.duration : dur);
    const onMeta = (): void => {
      if (isFinite(audio.duration) && audio.duration > 0) dur = audio.duration;
      if (audio.paused) timeEl.textContent = fmt(dur);
    };
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    const render = (): void => {
      const d = knownDur();
      const pct = d > 0 ? Math.min(100, (audio.currentTime / d) * 100) : 0;
      fill.style.width = `${pct}%`;
      knob.style.left = `${pct}%`;
      timeEl.textContent = fmt(audio.paused && audio.currentTime === 0 ? d : audio.currentTime);
    };
    audio.addEventListener('timeupdate', render);
    audio.addEventListener('play', () => {
      // Single-playback: stop every other clip and any live streamed audio so
      // two voice clips (or a clip + live reply) can never sound at once.
      this.pauseOtherClips(audio);
      try { this.onClipPlay(); } catch { /* */ }
      btn.innerHTML = WidgetUi.PAUSE_ICON;
      btn.setAttribute('aria-label', 'Pause');
      wrap.classList.add('playing');
    });
    audio.addEventListener('pause', () => {
      btn.innerHTML = WidgetUi.PLAY_ICON;
      btn.setAttribute('aria-label', 'Play');
      wrap.classList.remove('playing');
    });
    audio.addEventListener('ended', () => {
      btn.innerHTML = WidgetUi.PLAY_ICON;
      btn.setAttribute('aria-label', 'Play');
      wrap.classList.remove('playing');
      audio.currentTime = 0;
      fill.style.width = '0%';
      knob.style.left = '0%';
      timeEl.textContent = fmt(knownDur());
    });
    btn.addEventListener('click', () => {
      if (audio.paused) audio.play().catch(() => { /* autoplay/user-gesture */ });
      else audio.pause();
    });
    // Drag / click to seek along the track.
    const seekTo = (clientX: number): void => {
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return;
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const d = knownDur();
      if (d > 0) { audio.currentTime = ratio * d; render(); }
    };
    let dragging = false;
    track.addEventListener('pointerdown', (ev: PointerEvent) => {
      dragging = true;
      try { track.setPointerCapture(ev.pointerId); } catch { /* */ }
      seekTo(ev.clientX);
    });
    track.addEventListener('pointermove', (ev: PointerEvent) => { if (dragging) seekTo(ev.clientX); });
    const endDrag = (): void => { dragging = false; };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    wrap.appendChild(btn);
    wrap.appendChild(track);
    wrap.appendChild(timeEl);
    wrap.appendChild(audio);
    return wrap;
  }

  /** Pause every rendered voice clip except (optionally) one. */
  private pauseOtherClips(except?: HTMLAudioElement): void {
    if (!this.body) return;
    const players = this.body.querySelectorAll('.msg-audio audio');
    players.forEach((node) => {
      const a = node as HTMLAudioElement;
      if (a !== except && !a.paused) { try { a.pause(); } catch { /* */ } }
    });
  }

  /** Stop ALL rendered voice clips (used when live agent audio starts). */
  stopClipPlayback(): void { this.pauseOtherClips(); }

  /**
   * Realtime voice: arm the stop affordance for the agent's reply. With the
   * voice-control bar present the mic button itself turns red and doubles as
   * "tap to interrupt", so no separate ■ button is rendered.
   */
  showAgentStop(id?: string | null): void {
    this.wantAgentStop = true;
    if (this.voiceControlEl) this.attachAgentStop();
  }

  /** Attach the stop action to the stable voice-control bar. */
  private attachAgentStop(_bubble?: HTMLElement): void {
    if (!this.wantAgentStop) return;
    if (!this.voiceControlEl) return;
  }

  /** Hide and disarm the ■ STOP button (agent finished / was interrupted). */
  hideAgentStop(): void {
    this.wantAgentStop = false;
    if (this.agentStopBtn) { this.agentStopBtn.remove(); this.agentStopBtn = null; }
  }

  /** Build the per-message timestamp line, or null when disabled by config. */
  private makeTimeMeta(ts: string): HTMLElement | null {
    if (this.cfg.behavior?.showMessageTime === false) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    const label = d.toLocaleTimeString(this.cfg.language || undefined, { hour: '2-digit', minute: '2-digit' });
    return el('div', { class: 'msg-time' }, [label]);
  }

  /**
   * Attach reply audio to an already-rendered message (realtime voice streams
   * the audio after the transcript bubble is appended). No-op when the agent
   * audio player is disabled or the message already has one.
   */
  setMessageAudio(id: string, url: string): void {
    if (!this.body || !url) return;
    if (this.cfg.behavior?.showMessageAudio === false) return;
    const node = this.body.querySelector(`.msg[data-id="${CSS.escape(id)}"]`) as HTMLElement | null;
    if (!node || node.querySelector('.msg-audio')) return;
    const player = this.makeAudioPlayer(url);
    const time = node.querySelector('.msg-time');
    if (time) node.insertBefore(player, time);
    else node.appendChild(player);
    this.scrollToLatest();
  }

  /** Render an interactive / display rich-content card from a tool directive. */
  appendRichContent(directive: RichContentDirective): void {
    if (!this.body) return;
    this.hideTyping();
    const node = renderRichContent(directive, (toolCallId, formData) => {
      this.emit({ type: 'rich.submit', toolCallId, formData });
    });
    this.body.appendChild(node);
    this.scrollToLatest();
  }

  // ── Quick-reply pills ────────────────────────────────────────────────

  /**
   * Render the agent's quick-reply chips under the latest message. Only one
   * pill row is live at a time; a new row replaces the previous one.
   */
  renderPills(pills: WidgetPill[]): void {
    this.clearPills();
    const cfg: any = (this.cfg as any).pills;
    if (!this.body || !cfg?.enabled) return;
    if (this.modality !== 'text') return;
    // The server already capped the list using the IVA config's limit.
    const visible = pills;
    if (!visible.length) return;

    const row = el('div', { class: 'pill-row' });
    const outline = cfg.style === 'outline';
    for (const pill of visible) {
      const btn = el('button', { type: 'button', class: `pill${outline ? ' outline' : ''}` }, [pill.label]);
      btn.addEventListener('click', () => this.onPillClick(pill));
      row.appendChild(btn);
    }
    this.pillRowEl = row;
    this.body.appendChild(row);
    this.scrollToLatest();
  }

  /** Remove the live pill row (a pill was used, or the visitor typed instead). */
  clearPills(): void {
    if (this.pillRowEl) { this.pillRowEl.remove(); this.pillRowEl = null; }
    if (this.pillFormEl) { this.pillFormEl.remove(); this.pillFormEl = null; }
  }

  private onPillClick(pill: WidgetPill): void {
    if (pill.kind === 'tool' && pill.parameters && pill.parameters.length) {
      this.renderPillForm(pill);
      return;
    }
    this.clearPills();
    this.emit({ type: 'pill.click', pill });
  }

  /** Collect a tool pill's declared inputs before running it. */
  private renderPillForm(pill: WidgetPill): void {
    if (!this.body) return;
    if (this.pillRowEl) { this.pillRowEl.remove(); this.pillRowEl = null; }
    const wrap = el('div', { class: 'pill-form' });
    const readers: Array<() => [string, any]> = [];

    for (const p of pill.parameters || []) {
      const id = `pill-${pill.id}-${p.name}`;
      const label = el('label', { for: id }, [p.label || p.name]);
      let input: HTMLInputElement | HTMLSelectElement;
      if (p.options && p.options.length) {
        const sel = el('select', { id }) as HTMLSelectElement;
        for (const opt of p.options) sel.appendChild(el('option', { value: opt }, [opt]));
        if (p.defaultValue) sel.value = p.defaultValue;
        input = sel;
      } else if (p.type === 'boolean') {
        input = el('select', { id }) as HTMLSelectElement;
        input.appendChild(el('option', { value: 'true' }, ['✔']));
        input.appendChild(el('option', { value: 'false' }, ['✕']));
      } else {
        input = el('input', {
          id,
          type: p.type === 'number' ? 'number' : 'text',
          value: p.defaultValue || '',
          required: !!p.required,
        }) as HTMLInputElement;
      }
      label.appendChild(input);
      wrap.appendChild(label);
      readers.push(() => {
        const raw = (input as any).value ?? '';
        if (p.type === 'number') return [p.name, raw === '' ? undefined : Number(raw)];
        if (p.type === 'boolean') return [p.name, raw === 'true'];
        return [p.name, raw];
      });
    }

    const actions = el('div', { class: 'pill-form-actions' });
    const run = el('button', { type: 'button', class: 'pill' }, [pill.label]);
    run.addEventListener('click', () => {
      const parameters: Record<string, any> = {};
      for (const read of readers) {
        const [name, value] = read();
        if (value !== undefined && value !== '') parameters[name] = value;
      }
      const missing = (pill.parameters || []).some(p => p.required && parameters[p.name] === undefined);
      if (missing) return;
      this.clearPills();
      this.emit({ type: 'pill.click', pill, parameters });
    });
    const cancel = el('button', { type: 'button', class: 'pill outline' }, [this.tr('skip', 'Skip')]);
    cancel.addEventListener('click', () => this.clearPills());
    actions.appendChild(run);
    actions.appendChild(cancel);
    wrap.appendChild(actions);

    this.pillFormEl = wrap;
    this.body.appendChild(wrap);
    this.scrollToLatest();
  }

  updatePartial(role: 'agent' | 'user', text: string): void {
    if (!this.body) return;
    // Audio-only mode streams no agent text bubble.
    if (role === 'agent' && this.hideAgentText) return;
    this.hideLoading();
    if (!this.partialEl || this.partialEl.dataset.role !== role) {
      this.partialEl = el('div', { class: `msg ${role} partial` });
      this.partialEl.dataset.role = role;
      this.body.appendChild(role === 'agent' ? this.wrapAgentRow(this.partialEl) : this.partialEl);
    }
    this.partialEl.textContent = text;
    this.scrollToLatest();
  }

  commitPartial(role: 'agent' | 'user', text: string): void {
    if (role === 'agent' && this.hideAgentText) return;
    if (this.partialEl && this.partialEl.dataset.role === role) {
      this.partialEl.classList.remove('partial');
      if (role === 'agent') {
        this.partialEl.textContent = '';
        this.partialEl.appendChild(renderMarkdown(text));
        this.attachLinkPreview(this.partialEl, text);
      } else {
        this.partialEl.textContent = text;
      }
      const committed = this.partialEl;
      this.partialEl = null;
      if (role === 'agent') {
        const id = committed.dataset.id || `agent-${Date.now()}`;
        committed.dataset.id = id;
        this.attachFeedback(committed, id, text);
      }
    } else {
      this.appendMessage({
        id: `${role}-${Date.now()}`,
        role,
        text,
        ts: new Date().toISOString(),
      });
    }
  }

  /** True if an uncommitted partial bubble for `role` is currently rendered. */
  hasPartial(role: 'agent' | 'user'): boolean {
    return !!this.partialEl && this.partialEl.dataset.role === role;
  }

  /** Remove an orphaned partial bubble (e.g. a stray streamed fragment) without committing it. */
  discardPartial(role: 'agent' | 'user'): void {
    if (this.partialEl && this.partialEl.dataset.role === role) {
      this.rowOf(this.partialEl).remove();
      this.partialEl = null;
    }
  }

  /**
   * Remove ANY lingering uncommitted partial bubbles from the DOM — including
   * orphans no longer referenced by `this.partialEl`. When the model emits a
   * second text item in one turn, or a response is cancelled mid-stream,
   * `updatePartial` reassigns `this.partialEl` and leaves the previous `.partial`
   * node stranded (gray) in the DOM where neither `discardPartial` nor
   * `commitPartial` can reach it. Sweeping at every realtime turn boundary (after
   * the legit partial is already committed/discarded) guarantees no gray bubble
   * can survive into the next turn.
   */
  sweepStrayPartials(): void {
    if (!this.body) return;
    this.body.querySelectorAll('.msg.partial').forEach(n => this.rowOf(n).remove());
    this.partialEl = null;
  }

  setMicMuted(muted: boolean): void {
    this.micMuted = muted;
    if (this.micIcoEl) this.micIcoEl.textContent = muted ? '🔇' : '🎤';
    this.micBtn?.classList.toggle('muted', muted);
    // Hands-free has no recording overlay to drive the stage, so the mute state
    // is what tells the avatar whether we're listening.
    if (!this.agentSpeaking && this.micMode === 'mute') {
      this.setVoiceAvatarState(muted ? 'idle' : 'listening');
    }
    if (muted) this.setMicLevel(0);
    this.updateMicHint();
  }

  /**
   * Continuous open-mic: reflect whether the agent is verbalizing. While true
   * the mic is auto-muted and the button invites the visitor to barge in.
   */
  setAgentSpeaking(speaking: boolean): void {
    this.agentSpeaking = speaking;
    if (this.micBtn) this.micBtn.classList.toggle('agent-speaking', speaking);
    if (this.micIcoEl) this.micIcoEl.textContent = speaking ? '🔊' : this.micRecording ? '🔴' : this.micMuted ? '🔇' : '🎤';
    this.setVoiceAvatarState(speaking ? 'speaking' : (this.micMuted ? 'idle' : 'listening'));
    this.updateMicHint();
  }

  /**
   * Drive the avatar from agent playback alone. `setAgentSpeaking` also runs the
   * continuous open-mic anti-echo choreography, which must not fire in
   * push-to-talk — but the avatar has to react in every capture mode.
   */
  setAvatarSpeaking(speaking: boolean): void {
    if (speaking) { this.setVoiceAvatarState('speaking'); return; }
    if (this.agentSpeaking) return; // continuous mode still owns this turn
    this.setVoiceAvatarState(this.micRecording || !this.micMuted ? 'listening' : 'idle');
  }

  setVoiceAvatarActive(active: boolean): void {
    if (!this.voiceAvatarEl) return;
    this.voiceAvatarEl.hidden = !active;
    const immersive = active && this.voiceAvatarEl.classList.contains('immersive');
    this.voiceAvatarEl.parentElement?.classList.toggle('avatar-immersive', immersive);
    if (!active) this.setVoiceAvatarLevel(0);
  }

  setVoiceAvatarState(state: VoiceAvatarState): void {
    this.voiceAvatarState = state;
    this.voiceAvatar?.setState(state);
    const labels: Record<VoiceAvatarState, string> = {
      idle: this.tr('ready', 'Ready'),
      listening: this.tr('listeningShort', 'Listening'),
      thinking: this.tr('thinking', 'Thinking…'),
      speaking: this.tr('agentSpeakingShort', 'Agent speaking'),
    };
    if (this.voiceAvatarLabelEl) this.voiceAvatarLabelEl.textContent = labels[state];
  }

  setVoiceAvatarLevel(level: number): void { this.voiceAvatar?.setLevel(level); }

  /** Live caller input level (0..1) for the composer's mic meter. */
  setMicLevel(level: number): void {
    if (!this.micMeterEl) return;
    const active = Math.round(Math.min(1, Math.max(0, level)) * MIC_METER_SEGMENTS);
    const segments = this.micMeterEl.children;
    for (let i = 0; i < segments.length; i++) {
      (segments[i] as HTMLElement).classList.toggle('on', i < active);
    }
  }

  private toggleVoiceAvatarLayout(): void {
    if (!this.voiceAvatarEl) return;
    const immersive = !this.voiceAvatarEl.classList.contains('immersive');
    this.voiceAvatarEl.classList.toggle('immersive', immersive);
    this.voiceAvatarEl.classList.toggle('compact', !immersive);
    this.voiceAvatarEl.parentElement?.classList.toggle('avatar-immersive', immersive);
  }

  /** Show the connecting/loading panel (session, tools and knowledge spinning up). */
  showLoading(message: string): void {
    if (!this.body) return;
    if (!this.loadingEl) {
      this.loadingEl = el('div', { class: 'widget-loading' }) as HTMLDivElement;
      const spinner = el('div', { class: 'widget-loading-spinner' });
      const label = el('div', { class: 'widget-loading-text' }, [message]);
      this.loadingEl.appendChild(spinner);
      this.loadingEl.appendChild(label);
      this.body.appendChild(this.loadingEl);
    } else {
      const label = this.loadingEl.querySelector('.widget-loading-text');
      if (label) label.textContent = message;
    }
    // The expanded avatar layout hides the transcript; the connecting panel
    // still has to be visible, so surface it for the duration of the load.
    this.body.parentElement?.classList.add('loading');
    this.body.scrollTop = this.body.scrollHeight;
  }

  /** Remove the connecting/loading panel (first message or bridge ready). */
  hideLoading(): void {
    this.body?.parentElement?.classList.remove('loading');
    if (this.loadingEl) {
      this.loadingEl.remove();
      this.loadingEl = null;
    }
  }

  /** Near-RT push-to-talk recording feedback (🎤 idle ↔ 🔴 recording). */
  setMicRecording(recording: boolean): void {
    if (!this.micBtn) return;
    this.micRecording = recording;
    if (this.micIcoEl) this.micIcoEl.textContent = recording ? '🔴' : '🎤';
    this.micBtn.classList.toggle('recording', recording);
    if (!this.agentSpeaking) this.setVoiceAvatarState(recording ? 'listening' : 'thinking');
    // WhatsApp-style: reveal the recording overlay over the text bar + run timer.
    this.inputRowEl?.classList.toggle('recording', recording);
    if (recording) this.startRecTimer();
    else this.stopRecTimer();
    this.updateMicHint();
  }

  /**
   * Switch the mic control between the realtime mute toggle, the near-RT
   * tap-to-talk behaviour and the realtime hold-to-talk behaviour. Updates the
   * hold flag plus the visible instruction label/caption — the icon is driven
   * by setMicMuted (realtime) / setMicRecording (push-to-talk).
   */
  setMicMode(mode: 'mute' | 'push-to-talk' | 'hold-to-talk'): void {
    this.micHoldMode = mode === 'hold-to-talk';
    this.micMode = mode;
    this.updateMicHint();
  }

  /**
   * Refresh the mic button's visible label, tooltip and instruction caption so
   * the visitor always knows how to operate it (open mic / tap / hold) without
   * relying on a hover tooltip.
   */
  private updateMicHint(): void {
    const base = this.agentSpeaking
      ? this.tr('agentSpeaking', 'Agent speaking — tap to interrupt')
      : this.micRecording && this.micMode === 'hold-to-talk'
        ? this.tr('listeningRelease', 'Listening… release to send')
        : this.micRecording
          ? this.tr('recordingTap', 'Recording… tap to send')
      : this.micMode === 'hold-to-talk'
        ? this.tr('holdToSpeak', 'Hold to talk')
        : this.micMode === 'push-to-talk'
          ? this.tr('tapToTalk', 'Tap to talk')
          : this.micMuted
            ? this.tr('micMuted', 'Muted — tap to turn on')
            : this.tr('micOpen', 'Mic on — tap to mute');
    if (this.micBtn) this.micBtn.title = base;
    if (this.micLabelEl) this.micLabelEl.textContent = base;
  }

  setSendEnabled(enabled: boolean): void {
    if (this.sendBtn) this.sendBtn.disabled = !enabled;
  }

  setHumanChatMode(): void {
    this.humanChatMode = true;
    this.modality = 'text';
    this.hideAgentText = false;
    this.setMicRecording(false);
    this.setMicMuted(true);
    this.setVoiceAvatarActive(false);
    this.hideTyping();
    this.clearPills();
    this.clearFileChips();
    this.setFilesComposerState(false, false);
    this.inputRowEl?.classList.remove('has-voice-control', 'recording');
    if (this.voiceControlEl) this.voiceControlEl.style.display = 'none';
    if (this.fileBtn) this.fileBtn.style.display = 'none';
    if (this.fileInput) this.fileInput.disabled = true;
    if (this.inputEl) this.inputEl.maxLength = 8000;
    this.updateComposerMode();
  }

  clearSentDraft(text: string): void {
    if (this.inputEl?.value.trim() !== text.trim()) return;
    this.inputEl.value = '';
    this.autoGrowInput();
    this.updateComposerMode();
    this.emit({ type: 'draft.changed', text: '' });
  }

  getDraft(): string {
    return this.inputEl?.value || '';
  }

  restoreDraft(text: string): void {
    if (!this.inputEl || this.inputEl.value) return;
    this.inputEl.value = text;
    this.autoGrowInput();
    this.updateComposerMode();
  }

  /**
   * Reflect the current attachment state in the composer: keep the send button
   * disabled while any file is still uploading, and reveal it once at least one
   * ready attachment can be sent (even with no typed text).
   */
  setFilesComposerState(uploading: boolean, hasReadyFiles: boolean): void {
    this.uploadingFiles = uploading;
    this.hasReadyFiles = hasReadyFiles;
    this.setSendEnabled(!uploading);
    this.updateComposerMode();
  }

  upsertFileChip(f: WidgetUiFileEntry): void {
    if (!this.filesEl) return;
    let chip = this.fileChips.get(f.id);
    if (!chip) {
      chip = el('div', { class: `file-chip ${f.status}` }) as HTMLDivElement;
      this.filesEl.appendChild(chip);
      this.fileChips.set(f.id, chip);
    }
    chip.className = `file-chip ${f.status}`;
    chip.title = f.message || f.name;
    chip.textContent = '';
    const icon = f.status === 'uploading' ? '…' : f.status === 'ready' ? '✓' : '!';
    chip.appendChild(el('span', { class: 'file-chip-label' }, [`${icon} ${f.name}`]));
    const remove = el('button', {
      type: 'button',
      class: 'file-chip-remove',
      title: this.tr('remove', 'Remove'),
      'aria-label': this.tr('remove', 'Remove'),
      onClick: (ev: Event) => {
        ev.stopPropagation();
        const id = this.chipIdFor(chip as HTMLDivElement);
        if (id) this.emit({ type: 'file.remove', id });
      },
    }) as HTMLButtonElement;
    remove.textContent = '×';
    chip.appendChild(remove);
  }

  /** Reverse-lookup the current id for a chip (survives renameFileChip). */
  private chipIdFor(chip: HTMLDivElement): string | null {
    for (const [id, c] of this.fileChips) if (c === chip) return id;
    return null;
  }

  /** Remove a single file chip from the composer's files row. */
  removeFileChip(id: string): void {
    const chip = this.fileChips.get(id);
    if (!chip) return;
    chip.remove();
    this.fileChips.delete(id);
  }

  /** Clear every file chip (e.g. after the attachments were sent). */
  clearFileChips(): void {
    for (const chip of this.fileChips.values()) chip.remove();
    this.fileChips.clear();
    // Sweep anything that lost its map entry, so a stray chip can't survive a send.
    if (this.filesEl) this.filesEl.textContent = '';
  }

  renameFileChip(oldId: string, newId: string): void {
    const chip = this.fileChips.get(oldId);
    if (!chip) return;
    // The `file.ready` bridge event may already have created a chip under the
    // server id; without this the loser is orphaned in the DOM and renders as
    // a duplicate that no longer clears.
    const existing = this.fileChips.get(newId);
    if (existing && existing !== chip) existing.remove();
    this.fileChips.delete(oldId);
    this.fileChips.set(newId, chip);
  }

  /** Render the post-chat survey using the same field shape as the pre-call form. */
  renderPostcall(f: WidgetPrecallForm | undefined = this.cfg.postcallForm): void {
    this.clear();
    if (!f || !f.enabled || !f.fields?.length) {
      this.renderLauncher();
      return;
    }
    this.panel = el('div', { class: 'panel' });
    this.panel.appendChild(this.renderHeader());
    const readers: Record<string, () => string> = {};
    const wrap = el('div', { class: 'precall' });
    wrap.appendChild(el('h3', {}, [f.title || this.tr('postcallTitle', 'How was your experience?')]));
    if (f.description) wrap.appendChild(el('p', { class: 'form-description' }, [f.description]));
    for (const field of f.fields) {
      wrap.appendChild(this.renderPrecallField(field, readers));
    }
    const hasRequired = (f.fields || []).some(x => x.required);
    const submit = el('button', {
      class: 'submit',
      onClick: (ev: Event) => {
        ev.preventDefault();
        const collected = this.collectFormData(f.fields || [], readers);
        if (!collected) return;
        (submit as HTMLButtonElement).disabled = true;
        this.emit({ type: 'postcall.submit', data: collected });
      },
    }, [f.submitLabel || this.tr('submit', 'Submit')]);
    wrap.appendChild(submit);
    this.bindSubmitState(wrap, submit as HTMLButtonElement, f.fields || [], readers);
    if (!hasRequired) {
      wrap.appendChild(el('button', {
        class: 'skip',
        onClick: () => this.emit({ type: 'postcall.skip' }),
      }, [this.tr('skip', 'Skip')]));
    }
    this.panel.appendChild(wrap);
    this.root.appendChild(this.panel);
  }

  renderThankYou(message?: string, durationMs = 2000): void {
    this.clear();
    this.panel = el('div', { class: 'panel' });
    this.panel.appendChild(this.renderHeader());
    this.panel.appendChild(el('div', { class: 'thank-you' }, [message || this.tr('thankYou', 'Thanks for your feedback!')]));
    this.root.appendChild(this.panel);
    setTimeout(() => { this.renderLauncher(); }, durationMs);
  }

  private clear(): void {
    if (this.helpBubbleTimer !== null) window.clearTimeout(this.helpBubbleTimer);
    this.helpBubbleTimer = null;
    if (this.helpPhraseTimer !== null) window.clearTimeout(this.helpPhraseTimer);
    this.helpPhraseTimer = null;
    this.helpBubbleEl?.remove();
    this.helpBubbleEl = null;
    this.helpBubbleMessageEl = null;
    if (this.formSyncTimer !== null) { window.clearInterval(this.formSyncTimer); this.formSyncTimer = null; }
    this.voiceAvatar?.destroy();
    this.voiceAvatar = null;
    this.voiceAvatarEl = null;
    this.voiceAvatarLabelEl = null;
    this.panel = null;
    this.launcherEl = null;
    this.body = null;
    this.statusEl = null;
    this.statusDotEl = null;
    this.inputEl = null;
    this.sendBtn = null;
    this.micBtn = null;
    this.micIcoEl = null;
    this.micLabelEl = null;
    this.micMeterEl = null;
    this.voiceControlEl = null;
    this.agentStopBtn = null;
    this.inputRowEl = null;
    this.recOverlay = null;
    this.recTimerEl = null;
    this.loadingEl = null;
    this.fileBtn = null;
    this.fileInput = null;
    this.filesEl = null;
    this.fileChips.clear();
    this.partialEl = null;
    this.pillRowEl = null;
    this.pillFormEl = null;
    this.typingEl = null;
    this.pendingScrollToLatest = false;
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);
  }

  getModality(): WidgetModality { return this.modality; }
  setModality(m: WidgetModality): void { this.modality = m; }

  /** Rendered conversation, used as a fallback when the server copy isn't finalized yet. */
  getTranscript(): Array<{ role: string; text: string; timestamp?: string }> {
    return this.transcript
      .filter(entry => !entry.partial && entry.text)
      .map(entry => ({
        role: entry.role === 'agent' ? 'agent' : 'customer',
        text: entry.text,
        timestamp: entry.ts,
      }));
  }
}

function iconChat(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '26'); svg.setAttribute('height', '26');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
  const p = document.createElementNS(ns, 'path');
  p.setAttribute('d', 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z');
  svg.appendChild(p);
  return svg;
}

function iconDoor(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '16'); svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
  // Power / log-out hybrid: arrow exiting a box
  const p1 = document.createElementNS(ns, 'path');
  p1.setAttribute('d', 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4');
  const p2 = document.createElementNS(ns, 'polyline');
  p2.setAttribute('points', '16 17 21 12 16 7');
  const p3 = document.createElementNS(ns, 'line');
  p3.setAttribute('x1', '21'); p3.setAttribute('y1', '12');
  p3.setAttribute('x2', '9'); p3.setAttribute('y2', '12');
  svg.appendChild(p1); svg.appendChild(p2); svg.appendChild(p3);
  return svg;
}
